import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface AuditLog {
  id: string;
  timestamp: string | null;
  actor: string;
  action: string;
  resource: string;
  details: string;
  source: string;
}

interface AuditLogsResponse {
  items: AuditLog[];
  total: number;
  limit: number;
}

const formatDate = (value: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ data: AuditLogsResponse }>('/admin/audit-logs', {
        params: {
          limit: 50,
          ...(search ? { search } : {}),
        },
      });
      setLogs(response.data.data.items);
    } catch {
      setError('Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const applySearch = () => {
    setSearch(searchInput.trim());
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
            <p className="mt-1 text-gray-500">Audit events pulled from the API</p>
          </div>
          <button
            onClick={loadLogs}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>

        <div className="mb-6 rounded-lg bg-white p-4 shadow">
          <div className="flex gap-2">
            <input
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && applySearch()}
              placeholder="Search audit logs..."
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2"
            />
            <button
              onClick={applySearch}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Search
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg bg-white shadow">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading audit logs...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No audit events found
                      </td>
                    </tr>
                  ) : (
                    logs.map(log => (
                      <tr key={log.id}>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{log.actor}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{log.action}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{log.resource}</td>
                        <td className="max-w-lg px-6 py-4 text-sm text-gray-600">
                          {log.details}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{log.source}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;
